// chromecast-service.js
import chromecastIcon from '@/assets/chromecast.svg';

export default class ChromecastService {
    constructor(player, streamUrl) {
        this.player = player;
        this.streamUrl = streamUrl;
        this.castAvailable = false;
        this.isCasting = false;
        this.castButton = null;
        this.castContext = null;
        this.session = null;
        this.receiverName = null;
        this.originalPlayerState = {
            controls: true,
            poster: null
        };
    }

    // Example: If inside a class or module
    blockPlayHandler = () => {
        this.player.pause();
    };

    /**
     * Initialize the Chromecast service
     * @returns {Promise<boolean>} Whether initialization was successful
     */
    initialize() {
        return new Promise((resolve) => {
            // Check if Cast API is available
            if (!window.cast || !window.chrome) {
                resolve(false);
                return;
            }
            this.castContext = window.cast.framework.CastContext.getInstance();
            this.castContext.setOptions({
                receiverApplicationId: process.env.VUE_APP_CHROMECAST_ID,
                autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
                androidReceiverCompatible: true
            });
            this.setupEventListeners();
            this.castAvailable = true;
            const castState = this.castContext.getCastState();
            if (castState !== "NO_DEVICES_AVAILABLE") {
                this.addChromecastButton();
            }
            resolve(true);
        });
    }

    /**
     * Set up event listeners for Cast session state changes
     */
    setupEventListeners() {
        // Initialize RemotePlayer
        this.remotePlayer = new window.cast.framework.RemotePlayer();
        this.remotePlayerController = new window.cast.framework.RemotePlayerController(this.remotePlayer);
        this.isSyncingRemoteChange = false;
        this.isLocalChange = false; // Add this flag to prevent cascading events

        this.player.on('volumechange', () => {
            if (this.isCasting && !this.isLocalChange) {
                const isMuted = this.player.muted();
                if (this.remotePlayer.isMuted !== isMuted) {
                    this.isSyncingRemoteChange = true;
                    this.remotePlayerController.muteOrUnmute();

                    // Use timeout to reset flag instead of relying on event count
                    setTimeout(() => {
                        this.isSyncingRemoteChange = false;
                    }, 1000); // Give enough time for all Cast events to complete

                } else if (!isMuted) { // Only sync volume when not muted
                    const currentVolume = this.player.volume();
                    this.isSyncingRemoteChange = true;
                    this.remotePlayer.volumeLevel = currentVolume;
                    this.remotePlayerController.setVolumeLevel();

                    // Reset flag after volume operation
                    setTimeout(() => {
                        this.isSyncingRemoteChange = false;
                    }, 500);
                }
            }
        });
        this.remotePlayerController.addEventListener(
            window.cast.framework.RemotePlayerEventType.VOLUME_LEVEL_CHANGED,
            () => {
                if (!this.isSyncingRemoteChange && this.isCasting) {
                    const volume = this.remotePlayer.volumeLevel;

                    // Prevent cascading events
                    this.isLocalChange = true;
                    this.player.volume(volume);

                    // Reset local change flag
                    setTimeout(() => {
                        this.isLocalChange = false;
                    }, 100);
                }
            }
        );

        this.remotePlayerController.addEventListener(
            window.cast.framework.RemotePlayerEventType.IS_MUTED_CHANGED,
            () => {
                if (!this.isSyncingRemoteChange && this.isCasting) {
                    const isMuted = this.remotePlayer.isMuted;
                    this.isLocalChange = true;
                    this.player.muted(isMuted);

                    // Reset local change flag
                    setTimeout(() => {
                        this.isLocalChange = false;
                    }, 100);
                }
            }
        );

        this.castContext.addEventListener(
            window.cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
            (event) => {
                if (event.sessionState === "SESSION_STARTED") {
                    const session = this.castContext?.getCurrentSession?.();
                    if (session) {
                        this.loadMedia(session);
                    } else {
                        //console.warn('Cast session not available yet.');
                    }
                } else if (event.sessionState === "SESSION_ENDED") {
                    this.resumeLocalPlayback();
                }
            }
        );

        this.castContext.addEventListener(
            window.cast.framework.CastContextEventType.CAST_STATE_CHANGED,
            (event) => {
                if (event.castState !== "NO_DEVICES_AVAILABLE"){
                    this.addChromecastButton();
                }
            }
        );
        if (this.remotePlayer && this.remotePlayerController) {
            this.remotePlayerController.addEventListener(
                window.cast.framework.RemotePlayerEventType.IS_PAUSED_CHANGED,
                () => {
                    this.updatePlayButtonState();
                }
            );
            this.remotePlayerController.addEventListener(
                window.cast.framework.RemotePlayerEventType.IS_PLAYING_CHANGED,
                () => {
                    this.updatePlayButtonState();
                }
            );
        }
    }
    addCustomPlayButtonForCasting() {
        if (this.customPlayButton) {
            this.customPlayButton.style.display = 'block';
            return;
        }
        this.customPlayButton = document.createElement('button');
        this.customPlayButton.className = 'vjs-control vjs-button vjs-play-control vjs-playing';
        this.customPlayButton.title = 'Play/Pause (Chromecast)';
        const playIcon = document.createElement('span');
        playIcon.className = 'vjs-icon-placeholder';
        playIcon.setAttribute('aria-hidden', 'true');
        const textSpan = document.createElement('span');
        textSpan.className = 'vjs-control-text';
        textSpan.textContent = 'Play';

        this.customPlayButton.appendChild(playIcon);
        this.customPlayButton.appendChild(textSpan);

        // Add click handler
        this.customPlayButton.addEventListener('click', () => {
            if (this.isCasting && this.remotePlayerController) {
                this.remotePlayerController.playOrPause();
            }
        });

        const controlBar = this.player.el().querySelector('.vjs-control-bar');
        const firstChild = controlBar.firstChild;

        if (firstChild) {
            controlBar.insertBefore(this.customPlayButton, firstChild);
        } else {
            controlBar.appendChild(this.customPlayButton);
        }
        this.updatePlayButtonState();
    }

// Add this method to update the button state based on remotePlayer state
    updatePlayButtonState() {
        if (!this.customPlayButton || !this.remotePlayer) return;

        const textSpan = this.customPlayButton.querySelector('.vjs-control-text');

        if (this.remotePlayer.isPaused) {
            this.customPlayButton.classList.remove('vjs-playing');
            this.customPlayButton.classList.add('vjs-paused');
            textSpan.textContent = 'Play';
        } else {
            this.customPlayButton.classList.remove('vjs-paused');
            this.customPlayButton.classList.add('vjs-playing');
            textSpan.textContent = 'Pause';
        }
    }
    /**
     * Add Chromecast button to video player
     */
    addChromecastButton() {
        if (!this.castAvailable) return;
        const existingButtons = document.getElementsByClassName('vjs-chromecast-button');
        if (existingButtons && existingButtons.length > 0) {
            return;
        }

        this.castButton = document.createElement('chrome-cast-button');
        this.castButton.className = 'vjs-control vjs-button vjs-chromecast-button';
        this.castButton.title = 'Cast to Chromecast';

        // Create an <img> element for the SVG
        const iconElement = document.createElement('img');
        iconElement.src = chromecastIcon;
        iconElement.alt = 'Chromecast Icon';
        iconElement.style.width = '18px';
        iconElement.style.height = '18px';

        // Add the icon to the button
        this.castButton.appendChild(iconElement);

        // Add click handler
        this.castButton.addEventListener('click', () => {
            this.startCasting();
        });

        // Find the control bar and add the button
        const controlBar = this.player.el().querySelector('.vjs-control-bar');

        // Insert before the fullscreen button (usually last)
        const fullscreenButton = controlBar.querySelector('.vjs-fullscreen-control');
        if (fullscreenButton) {
            controlBar.insertBefore(this.castButton, fullscreenButton);
        } else {
            controlBar.appendChild(this.castButton);
        }
    }

    hidePlayerControls() {
        if (!this.player) return;
        const controlBar = this.player.getChild('ControlBar');
        controlBar.getChild('FullscreenToggle')?.hide();
        controlBar.getChild('PictureInPictureToggle')?.hide();
        controlBar.getChild('PlayToggle')?.hide();
        this.addCustomPlayButtonForCasting();

        const qualityButton = this.player.el().querySelector('.vjs-quality-selector');
        if (qualityButton) qualityButton.style.display = 'none';
    }

    showPlayerControls() {
        if (!this.player) return;
        this.player.off('play', this.blockPlayHandler);

        const controlBar = this.player.getChild('ControlBar');
        controlBar.getChild('FullscreenToggle')?.show();
        controlBar.getChild('PictureInPictureToggle')?.show();
        controlBar.getChild('PlayToggle')?.show();

        if (this.customPlayButton) {
            this.customPlayButton.remove();
            this.customPlayButton = null;
        }
        const qualityButton = this.player.el().querySelector('.vjs-quality-selector');
        if (qualityButton) qualityButton.style.display = '';
    }

    sendAccessToken() {
        if (!this.castContext) {
            return;
        }
        //const session = this.castContext.getCurrentSession();
        if (!this.session) {
            return;
        }
        const accessToken = localStorage.getItem('access_token')

        // Convert the object to a JSON string
        const message = JSON.stringify({ token: accessToken });

        this.session.sendMessage('urn:x-cast:com.procricket.token', message)
    }

    /**
     * Remove the Chromecast button
     */
    removeChromecastButton() {
        if (this.castButton && this.castButton.parentNode) {
            this.castButton.parentNode.removeChild(this.castButton);
            this.castButton = null;
        }
    }

    /**
     * Clean up resources when player is disposed
     * Call this method when your player is being destroyed
     */
    dispose() {
        // Remove event listeners
        if (this.castContext) {
            if (this.sessionStateChangeListener) {
                this.castContext.removeEventListener(
                    window.cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
                    this.sessionStateChangeListener
                );
            }

            if (this.castStateChangeListener) {
                this.castContext.removeEventListener(
                    window.cast.framework.CastContextEventType.CAST_STATE_CHANGED,
                    this.castStateChangeListener
                );
            }
            if (this.remotePlayerController) {
                this.remotePlayerController.removeEventListener(
                    window.cast.framework.RemotePlayerEventType.IS_PAUSED_CHANGED
                );
                this.remotePlayerController.removeEventListener(
                    window.cast.framework.RemotePlayerEventType.IS_PLAYING_CHANGED
                );
            }
        }

        // Remove the button if it exists
        this.removeChromecastButton();

        this.stopCasting();

        // Reset state
        this.castAvailable = false;
        this.player = null;
        this.castContext = null;
        this.sessionStateChangeListener = null;
        this.castStateChangeListener = null;
    }
    /**
     * Start casting to Chromecast device
     */
    startCasting() {
        if (!this.castAvailable) {
            return;
        }
        this.player.on('play', this.blockPlayHandler);
        // Request a session if none exists
        sessionStorage.setItem('chromecastStarted', true);
        this.castContext.requestSession().then((session) => {
            this.loadMedia(session);
            this.sendAccessToken();
        }).catch((error) => {
            //console.error("❌ Failed to start Cast session:", error);
        });
    }

    /**
     * Load media onto Chromecast device
     * @param {Object} session - Chromecast session object
     */
    loadMedia(session) {
        if (!session) {
            //console.warn("❌ No active cast session found.");
            return;
        }
        this.session = session;
        this.savePlayerState();
        this.receiverName = session.getCastDevice().friendlyName;
        this.disableLocalPlayer();
        const sk = localStorage.getItem('sk');
        const urlWithSk = this.appendSkToUrl(this.streamUrl, sk);

        const mediaInfo = new window.chrome.cast.media.MediaInfo(
            urlWithSk,
            'application/x-mpegURL'
        );
        mediaInfo.streamType = window.chrome.cast.media.StreamType.LIVE;
        mediaInfo.liveSeekableRange = null;
        mediaInfo.eventTitle = localStorage.getItem('eventTitle');
        //mediaInfo.isLiveContent = true;

        const request = new window.chrome.cast.media.LoadRequest(mediaInfo);
        session.loadMedia(request).then(() => {
            this.isCasting = true;
            const volume = this.remotePlayer.volumeLevel;
            this.player.volume(volume); // Sync to Video.js player
            // Update Chromecast button to show active state
            if (this.castButton) {
                this.castButton.classList.add('casting-active');
            }

        }).catch((error) => {
            this.resumeLocalPlayback();
        });
    }

    /**
     * Save the current state of the video player
     */
    savePlayerState() {
        this.originalPlayerState = {
            controls: this.player.controls(),
            poster: this.player.poster(),
            currentTime: this.player.currentTime(),
            source: this.player.currentSrc()
        };
    }

    /**
     * Disable the local player while casting
     */
    disableLocalPlayer() {
        this.player.pause();
        this.addCastingOverlay();
        this.hidePlayerControls();

        this.isCasting = true;
    }

    addCastingOverlay() {
        this.removeCastingOverlay();

        const playerWrapper = document.querySelector("#VideoJS-player").closest('.video-js');
        if (!playerWrapper) return;

        // Hide the <video> element (techEl)
        const techEl = playerWrapper.querySelector('.vjs-tech');
        if (techEl) {
            techEl.style.display = 'none';
        }
        // Make sure wrapper is relatively positioned
        playerWrapper.style.position = 'relative';

        const overlay = document.createElement('div');
        overlay.id = 'casting-overlay';
        overlay.style.position = 'absolute';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.right = '0';
        overlay.style.bottom = '0';
        overlay.style.display = 'flex';
        overlay.style.flexDirection = 'column';
        overlay.style.justifyContent = 'center';
        overlay.style.alignItems = 'center';
        overlay.style.backgroundColor = 'rgba(0, 0, 0, 0)';
        overlay.style.pointerEvents = 'none';
        overlay.style.zIndex = '10';

        const castText = document.createElement('div');
        castText.textContent = 'Now Casting to';
        castText.style.fontSize = '20px';
        castText.style.color = 'white';
        castText.style.pointerEvents = 'auto';

        const deviceNameText = document.createElement('div');
        deviceNameText.textContent = `${this.receiverName}`;
        deviceNameText.style.fontSize = '30px';
        deviceNameText.style.color = 'white';
        deviceNameText.style.paddingTop = '10px';
        deviceNameText.style.pointerEvents = 'auto';


        overlay.appendChild(castText);
        overlay.appendChild(deviceNameText);

        playerWrapper.appendChild(overlay);
    }

    /**
     * Remove the casting overlay
     */
    removeCastingOverlay() {
        const overlay = this.player.el().querySelector('#casting-overlay');
        if (overlay) {
            overlay.remove();
        }
        const playerWrapper = document.querySelector("#VideoJS-player").closest('.video-js');
        const techEl = playerWrapper.querySelector('.vjs-tech');
        if (techEl) {
            techEl.style.display = '';
        }
    }

    /**
     * Resume local playback after casting ends
     */
    resumeLocalPlayback() {
        if (!this.isCasting) return;
        this.isCasting = false;
        this.removeCastingOverlay();
        this.showPlayerControls();
        sessionStorage.setItem('chromecastStarted', false);
        // Restore original controls state
        if (this.originalPlayerState) {
            this.player.controls(this.originalPlayerState.controls);
            if (this.originalPlayerState.currentTime) {
                try {
                    this.player.currentTime(this.originalPlayerState.currentTime);
                } catch (e) {
                    console.warn('Could not restore playback position:', e);
                }
            }
        } else {
            this.player.controls(true);
        }
        this.player.play().catch(error => {
            console.warn('Failed to auto-play after casting:', error);
        });
        if (this.castButton) {
            this.castButton.classList.remove('casting-active');
        }
    }

    /**
     * Stop the current casting session
     */
    stopCasting() {
        if (!this.isCasting || !this.castContext) return;
        const session = this.castContext.getCurrentSession();
        if (session) {
            this.session.endSession(true);
            this.resumeLocalPlayback();
        }
    }

    appendSkToUrl(url, sk) {
        if (!sk) return url;
        const urlObj = new URL(url, window.location.origin);
        urlObj.searchParams.set('sk', sk);
        return urlObj.toString();
    }
}

